import ConditionalToolTip from "components/design/conditionalToolTip";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { AvatarGroup } from "components/DesignSystem/AvatarGroup/AvatarGroup";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { MultiSelectFilter } from "components/DesignSystem/Filter/Filter";
import { ArrowDown } from "components/icons/ArrowDown";
import { INKLE_TEAM_AND_CPA_TEAM } from "constants/chatType";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useUpdateChatTicketMutation } from "store/apis/chatTicket";
import { useGetServiceTeamQuery } from "store/apis/serviceTeam";
import { ChatTicket } from "types/Models/chatTicket";
import { BackendError } from "types/utils/error";

export const TicketAssigneeDropdown = ({
  currentTicketAssignees,
  ticketId,
}: {
  currentTicketAssignees: ChatTicket["assignee"];
  ticketId: string;
}) => {
  const { isCustomer, canManageInternalTickets } = useRoleBasedView();
  const { data: assignees = [] } = useGetServiceTeamQuery(
    {
      accessible_teams: canManageInternalTickets
        ? INKLE_TEAM_AND_CPA_TEAM
        : null,
    },
    {
      skip: isCustomer,
    }
  );
  const [updateTicket, { isLoading: isTicketUpdating }] =
    useUpdateChatTicketMutation();
  const { alertToast } = useToast();

  const handleAssignees = async (newValues: string[]) => {
    try {
      await updateTicket({
        assignee_profile_ids: newValues.join(","),
        resolved_status: null,
        ticket_id: ticketId,
      }).unwrap();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <button className="all:unset t-flex t-items-center t-justify-between">
            <ConditionalToolTip
              condition={
                currentTicketAssignees.length > 0 && (
                  <div>
                    {currentTicketAssignees.map(({ assignee_name }) => (
                      <div key={assignee_name} className="t-text-left">
                        {assignee_name}
                      </div>
                    ))}
                  </div>
                )
              }
            >
              <div className="t-min-w-14" onClick={(e) => e.stopPropagation()}>
                <AvatarGroup max={2} variant="no-fill">
                  {currentTicketAssignees.map(
                    ({
                      assignee_name,
                      assignee_profile_url,
                      assignee_uuid,
                    }) => (
                      <div
                        className="t-rounded-full t-ring-white t-ring-2"
                        key={assignee_uuid}
                      >
                        <Avatar
                          alt={assignee_name}
                          src={assignee_profile_url || ""}
                        />
                      </div>
                    )
                  )}
                </AvatarGroup>
              </div>
            </ConditionalToolTip>

            <ArrowDown />
          </button>
        </Dropdown.Trigger>
        <Dropdown.Content className="t-h-80 t-p-3" align="start" sideOffset={8}>
          <MultiSelectFilter
            options={assignees.map(({ name, uuid }) => ({
              label: name,
              value: uuid,
              disabled: isTicketUpdating,
            }))}
            selected={currentTicketAssignees.map(
              ({ assignee_uuid }) => assignee_uuid
            )}
            onChange={handleAssignees}
            isSearchable
            isSelectAll={false}
          />
        </Dropdown.Content>
      </Dropdown.Root>
    </div>
  );
};
